import { Controller } from "@hotwired/stimulus"

// 仕様書 §3.1.4 動画自動再生（自動再生・ミュート開始）に対応する playerVars。
// `mute: 1` はブラウザの自動再生制限への保険であり、仕様準拠を兼ねる。
export const PLAYER_VARS = Object.freeze({
  autoplay: 1,
  mute: 1,
  rel: 0
})

// YouTube ファサードパターンコントローラ
//
// 初期表示時はサムネイル画像 + 再生ボタンのみ表示し、
// ユーザーが再生ボタンをクリックした時点で YouTube IFrame API を読み込む。
// video_play / video_progress イベントを dataLayer に push する。
export default class extends Controller {
  static values = {
    videoId: String,
    articleId: String,
    articleTitle: String
  }

  static targets = ["thumbnail", "player"]

  connect() {
    this.playTracked = false
    this.trackedMilestones = new Set()
    this.progressInterval = null
    this.player = null
  }

  disconnect() {
    this.#stopProgressTracking()
    if (this.player && typeof this.player.destroy === "function") {
      this.player.destroy()
    }
  }

  play() {
    // サムネイルを非表示にし、プレーヤーコンテナを表示
    this.thumbnailTarget.classList.add("hidden")
    this.playerTarget.classList.remove("hidden")

    this.#loadYouTubeAPI().then(() => {
      this.#createPlayer()
    })
  }

  // --- private ---

  #loadYouTubeAPI() {
    return new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve()
        return
      }

      // グローバルコールバックを設定
      const existingCallback = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => {
        if (existingCallback) existingCallback()
        resolve()
      }

      // API スクリプトが未ロードの場合のみ追加
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const script = document.createElement("script")
        script.src = "https://www.youtube.com/iframe_api"
        document.head.appendChild(script)
      }
    })
  }

  #createPlayer() {
    this.player = new window.YT.Player(this.playerTarget, {
      videoId: this.videoIdValue,
      playerVars: PLAYER_VARS,
      events: {
        onStateChange: (event) => this.#onPlayerStateChange(event)
      }
    })
  }

  #onPlayerStateChange(event) {
    if (event.data === window.YT.PlayerState.PLAYING) {
      if (!this.playTracked) {
        this.playTracked = true
        this.#pushDataLayer({
          event: "video_play",
          video_url: `https://www.youtube.com/watch?v=${this.videoIdValue}`,
          video_title: this.articleTitleValue,
          article_id: this.articleIdValue,
          article_title: this.articleTitleValue
        })
      }
      this.#startProgressTracking()
    }

    if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
      this.#stopProgressTracking()
    }
  }

  #startProgressTracking() {
    if (this.progressInterval) return

    this.progressInterval = setInterval(() => {
      this.#checkProgress()
    }, 1000)
  }

  #stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
  }

  #checkProgress() {
    if (!this.player || typeof this.player.getCurrentTime !== "function") return

    const current = this.player.getCurrentTime()
    const duration = this.player.getDuration()
    if (duration === 0) return

    const percent = Math.floor((current / duration) * 100)
    const milestones = [25, 50, 75, 100]

    for (const milestone of milestones) {
      if (percent >= milestone && !this.trackedMilestones.has(milestone)) {
        this.trackedMilestones.add(milestone)
        this.#pushDataLayer({
          event: "video_progress",
          video_url: `https://www.youtube.com/watch?v=${this.videoIdValue}`,
          video_percent: milestone,
          article_id: this.articleIdValue
        })
      }
    }
  }

  #pushDataLayer(data) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(data)
  }
}
